import { useMemo } from 'react';
import { useSelector } from 'react-redux';
// Shared with the matcher so the ceiling can't drift from the backend's
// peak cap (loudnessLimits is dependency-free for this reason).
import { headroomDb } from '../../backend/loudnessLimits';

/**
 * The analyzed rows in the Loudness tab's CURRENT view — the LUFS range
 * filter applied. This is exactly the set a Match run acts on ("what you see
 * is what runs"), so both the settings hint and the run itself must reason
 * about the same rows. Kept here so neither component re-derives the scope.
 */
export function useVisibleLoudnessRows() {
    const logs = useSelector((state) => state.loudnessLogs.logs);
    const min = useSelector((state) => state.loudnessSettings.loudnessFilterMin);
    const max = useSelector((state) => state.loudnessSettings.loudnessFilterMax);
    return useMemo(() => logs.filter((log) => {
        if (!log || typeof log !== 'object' || log === '\n') return false;
        if (log.status === 'summary' || log.status === 'separator') return false;
        if (log.status === 'analyze-loudness' && typeof log.loudness === 'number') {
            return log.loudness >= min && log.loudness <= max;
        }
        return true;
    }), [logs, min, max]);
}

/**
 * The loudest level EVERY track in scope can reach without clipping.
 *
 * Loudness matching is a flat gain and BOOSTS are capped so the produced
 * file's true peak stays under PEAK_CEILING_DBFS, so each track's own
 * ceiling is `loudness + max(0, headroom)`; the set's ceiling is the lowest
 * of those. Aim above it and the limiting tracks stop short and end up
 * quieter than the rest — which defeats the feature's whole promise ("these
 * all end up the same").
 *
 * The max(0, …) mirrors the matcher's cappedGainDb: attenuation is never
 * limited (it only lowers peaks), so a track whose native peak already
 * exceeds the ceiling can still reach any target at or below its CURRENT
 * loudness. Without the clamp such a track poisons the whole hint — it
 * reports a ceiling below the set's quietest track and flags runs that are
 * pure attenuation.
 *
 * Returns null until peaks are known for at least one row. Otherwise:
 *   lufs       — rounded DOWN so it is always actually achievable
 *   limitedBy  — the row that sets the ceiling
 *   trackCount — analyzed rows in scope
 *   shortRows  — rows that would fall short of `target` (empty when the
 *                target is at or below the ceiling)
 */
export function useReachableLoudness(rows, target) {
    return useMemo(() => {
        let best = null;
        let trackCount = 0;
        const analyzed = [];
        for (const l of rows) {
            if (l?.status !== 'analyze-loudness' || typeof l.loudness !== 'number') continue;
            trackCount++;
            analyzed.push(l);
            const room = headroomDb(l.truePeak);
            if (!isFinite(room)) continue;          // peak unknown — can't limit the set
            const max = l.loudness + Math.max(0, room);
            if (best === null || max < best.lufs) best = { lufs: max, row: l };
        }
        if (!best) return null;
        const t = Number(target);
        const shortRows = Number.isFinite(t)
            ? analyzed.filter(l => l.loudness + Math.max(0, headroomDb(l.truePeak)) < t)
            : [];
        return {
            lufs: Math.floor(best.lufs * 10) / 10,
            limitedBy: best.row,
            trackCount,
            shortRows,
        };
    }, [rows, target]);
}
